import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { ArrowLeft, Mail, MessageCircle, Pencil, Phone, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ErrorState, LoadingState } from '@/components/composites/states'
import { cn } from '@/lib/utils'
import { paths } from '@/routes/paths'
import { useCrm2Contact, useCrm2Contacts, type ContactPage as ContactPageData } from './api'
import { EditContact } from './EditContact'
import { DealSheet } from './DealSheet'
import { Timeline } from './Timeline'
import { Crm2Frame, Dot, LINK_CHIP, ROLE_LABEL, Section, usePeopleNames, whatsappLink } from './shared'

/** Everyone at every account, so a person can be found without remembering their company. */
export function ContactsPage() {
  const { workspaceSlug = '' } = useParams()
  const [q, setQ] = useState('')
  const list = useCrm2Contacts(q.trim())
  const rows = list.data ?? []
  return (
    <Crm2Frame title="Contacts" description="Every person at every account. Open one to see each conversation and deal they are part of.">
      <div className="mb-3 relative w-fit">
        <Search className="pointer-events-none absolute left-2 top-2.5 size-4 text-text-muted" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, email, number or company" className="h-9 w-72 pl-8" aria-label="Find a contact" />
      </div>
      {list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : list.isPending ? (
        <LoadingState label="Loading contacts" />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm">
          <table className="w-full text-sm">
            <thead className="border-b border-border-soft text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Title</th>
                <th className="px-4 py-2 font-medium">Account</th>
                <th className="px-4 py-2 font-medium">Email</th>
                <th className="px-4 py-2 font-medium">Contact number</th>
                <th className="px-4 py-2 text-right font-medium">Deals</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-soft">
              {rows.map((c) => (
                <tr key={c.id} className="hover:bg-accent/40">
                  <td className="px-4 py-2">
                    <Link to={paths.crm2Contact(workspaceSlug, c.id)} className={cn(LINK_CHIP, 'text-sm')}>
                      {c.name}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{c.title ?? '—'}</td>
                  <td className="px-4 py-2">
                    {c.accountName ? (
                      <Link to={paths.crm2Account(workspaceSlug, c.accountId)} className={LINK_CHIP}>
                        {c.accountName}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{c.email ?? '—'}</td>
                  <td className="px-4 py-2 text-muted-foreground">{c.phone ?? '—'}</td>
                  <td className="num px-4 py-2 text-right">{c.deals}</td>
                </tr>
              ))}
              {!rows.length ? (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                    Nobody found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}
    </Crm2Frame>
  )
}

/** One person: who they are, the deals they are on, and everything said with them. */
export function ContactPage() {
  const { contactId = '' } = useParams()
  const [params, setParams] = useSearchParams()
  const q = useCrm2Contact(contactId)
  const openId = params.get('deal')
  const open = (id: string | null) => {
    const next = new URLSearchParams(params)
    if (id) next.set('deal', id)
    else next.delete('deal')
    setParams(next, { replace: true })
  }
  return (
    <div className="mx-auto w-full max-w-[1400px]">
      {q.error ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : q.isPending ? <LoadingState label="Loading the contact" /> : <Person d={q.data} onOpen={open} />}
      <DealSheet id={openId} onClose={() => open(null)} onOpen={open} />
    </div>
  )
}

function Person({ d, onOpen }: { d: ContactPageData; onOpen: (id: string) => void }) {
  const { workspaceSlug = '' } = useParams()
  const name = usePeopleNames()
  const c = d.contact
  const [only, setOnly] = useState(true)
  const [editing, setEditing] = useState(false)
  const items = only ? d.history.filter((h) => h.withThem) : d.history
  return (
    <div className="grid gap-4 lg:grid-cols-[20rem_minmax(0,1fr)]">
      <div className="grid content-start gap-4">
        <section className="grid gap-2 rounded-xl border border-border bg-card p-4 shadow-sm">
          <Link to={paths.crm2Contacts(workspaceSlug)} className={LINK_CHIP}>
            <ArrowLeft className="size-4" />
            Contacts
          </Link>
          <div className="flex items-center gap-3">
            <span className="grid size-12 shrink-0 place-items-center rounded-full bg-muted text-lg font-semibold">{c.name.slice(0, 1).toUpperCase()}</span>
            <div className="min-w-0">
              <h1 className="truncate text-lg font-semibold">{c.name}</h1>
              {c.title ? <p className="truncate text-sm text-muted-foreground">{c.title}</p> : null}
            </div>
            {!editing ? (
              <Button size="sm" variant="outline" className="ml-auto shrink-0" onClick={() => setEditing(true)} aria-label={`Edit ${c.name}`}>
                <Pencil className="size-4" />
                Edit
              </Button>
            ) : null}
          </div>
          {d.account ? (
            <Link to={paths.crm2Account(workspaceSlug, d.account.id)} className={cn(LINK_CHIP, 'text-sm')}>
              {d.account.name}
            </Link>
          ) : null}
          {editing ? (
            <EditContact c={c} onDone={() => setEditing(false)} />
          ) : (
            <>
              {c.email ? (
                <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 text-sm hover:underline">
                  <Mail className="size-4 text-muted-foreground" />
                  {c.email}
                </a>
              ) : null}
              {c.phone ? (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1.5 hover:underline">
                    <Phone className="size-4 text-muted-foreground" />
                    {c.phone}
                  </a>
                  {whatsappLink(c.phone) ? (
                    <a href={whatsappLink(c.phone)!} target="_blank" rel="noreferrer" className={LINK_CHIP}>
                      <MessageCircle className="size-4" />
                      WhatsApp
                    </a>
                  ) : (
                    <span className="text-xs text-warning">Add the country code for WhatsApp</span>
                  )}
                </div>
              ) : (
                <button type="button" onClick={() => setEditing(true)} className={LINK_CHIP}>
                  <Phone className="size-4" />
                  Add a contact number
                </button>
              )}
            </>
          )}
        </section>
        <Section title="Deals they are on" count={d.deals.length} empty="Not named on any deal yet.">
          <ul className="divide-y divide-border-soft">
            {d.deals.map((x) => (
              <li key={x.id}>
                <button type="button" onClick={() => onOpen(x.id)} className="grid w-full gap-0.5 px-4 py-2.5 text-left text-sm hover:bg-accent">
                  <span className={cn(LINK_CHIP, 'gap-1.5 text-sm')}>
                    {x.status === 'open' ? <Dot level={x.fresh.level} /> : null}
                    {x.name}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {x.status === 'open' ? `${x.groupLabel}${x.statusName ? ` · ${x.statusName}` : ''}` : x.status === 'won' ? 'Won' : 'Lost'}
                    {x.role ? ` · ${ROLE_LABEL[x.role] ?? x.role}` : ''} · {name(x.ownerId)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Section>
        <Section title="Colleagues" count={d.colleagues.length} empty="Nobody else at this account yet.">
          <ul className="divide-y divide-border-soft">
            {d.colleagues.map((p) => (
              <li key={p.id} className="px-4 py-2 text-sm">
                <Link to={paths.crm2Contact(workspaceSlug, p.id)} className={cn(LINK_CHIP, 'text-sm')}>
                  {p.name}
                </Link>
                {p.title ? <span className="block text-xs text-muted-foreground">{p.title}</span> : null}
              </li>
            ))}
          </ul>
        </Section>
      </div>
      <div className="grid min-w-0 content-start gap-2">
        <div className="inline-flex w-fit rounded-lg border border-border bg-surface p-0.5 text-sm" role="group" aria-label="Which history">
          <button type="button" aria-pressed={only} onClick={() => setOnly(true)} className={cn('rounded-md px-3 py-1', only ? 'bg-accent font-medium' : 'text-muted-foreground')}>
            With {c.name.split(' ')[0]}
          </button>
          <button type="button" aria-pressed={!only} onClick={() => setOnly(false)} className={cn('rounded-md px-3 py-1', !only ? 'bg-accent font-medium' : 'text-muted-foreground')}>
            Also their deals
          </button>
        </div>
        {d.account ? (
          <Timeline accountId={d.account.id} items={items} contacts={[{ id: c.id, name: c.name }]} defaultContactId={c.id} title={`Conversations and history with ${c.name}`} />
        ) : (
          <p className="text-sm text-muted-foreground">This contact has no account.</p>
        )}
      </div>
    </div>
  )
}

